<template>
  <transition name="instructor-modal-fade" appear>
    <div
      class="instructor-modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`instructor-name-${instructor.id}`"
      @click.self="$emit('close')"
    >
      <div class="instructor-modal__dialog">
        <button
          class="instructor-modal__close"
          type="button"
          :aria-label="$t('general.close')"
          @click="$emit('close')"
        >
          &times;
        </button>

        <div class="instructor-modal__scroll-area">
          <div class="instructor-modal__profile">
            <img
              :src="instructor.photo"
              :alt="instructor.name"
              class="instructor-modal__photo"
              @error="useFallbackPhoto"
            >
            <div class="instructor-modal__identity">
              <span class="instructor-modal__eyebrow">{{ $t("general.instructor") }}</span>
              <h2 :id="`instructor-name-${instructor.id}`">{{ instructor.name }}</h2>
              <p class="instructor-modal__title">{{ instructorTypeLabel }}</p>
              <p class="instructor-modal__bio">
                {{ instructor.bio || $t("general.instructor_information_not_found") }}
              </p>
            </div>
          </div>

          <div class="instructor-modal__trainings">
            <h3>{{ $t("general.trainings") }}</h3>
            <div v-if="trainings.length" class="instructor-training-list">
              <router-link
                v-for="training in trainings"
                :key="training.id"
                :to="assignmentRoute(training)"
                class="instructor-training"
                @click.native="$emit('close')"
              >
                <strong>{{ training.name || training.title }}</strong>
                <span class="instructor-training__status">{{ assignmentStatus(training) }}</span>
              </router-link>
            </div>
            <p v-else class="instructor-modal__empty">
              {{ $t("general.no_active_assignment_for_instructor") }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import noPhoto from "@/assets/images/no-photo.png";

export default {
  name: "InstructorDetailModal",
  props: {
    instructor: {
      type: Object,
      required: true,
    },
  },
  computed: {
    trainings() {
      return Array.isArray(this.instructor.trainings)
        ? this.instructor.trainings
        : [];
    },
    instructorTypeLabel() {
      return this.instructor.type === "external"
        ? this.$t("general.external_instructor")
        : this.$t("general.internal_instructor");
    },
  },
  mounted() {
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    handleKeydown(event) {
      if (event.key === "Escape") this.$emit("close");
    },
    useFallbackPhoto(event) {
      if (event.target.dataset.fallbackApplied) return;
      event.target.dataset.fallbackApplied = "true";
      event.target.src = noPhoto;
    },
    assignmentRoute(training) {
      return {
        name: "user.assignment-detail",
        params: { id: training.id },
      };
    },
    assignmentStatus(training) {
      const status = training.action_label || "assigned";
      return this.$t(`general.${status}`);
    },
  },
};
</script>

<style scoped>
.instructor-modal {
  position: fixed;
  inset: 0;
  z-index: 11000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(22, 30, 50, 0.64);
}

.instructor-modal__dialog {
  position: relative;
  width: min(720px, 100%);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(17, 29, 58, 0.24);
}

.instructor-modal__scroll-area {
  max-height: calc(100vh - 60px);
  margin: 6px;
  padding: 22px;
  padding-right: 30px;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.instructor-modal__close {
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 1;
  width: 36px;
  height: 36px;
  border: 1px solid #dce3ef;
  border-radius: 50%;
  color: #667085;
  background: #f8fafc;
  font-size: 24px;
  line-height: 30px;
  cursor: pointer;
}

.instructor-modal__profile {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 24px;
  align-items: center;
  padding-right: 32px;
}

.instructor-modal__photo {
  width: 180px;
  height: 180px;
  border-radius: 14px;
  object-fit: cover;
  background: #eef1f5;
}

.instructor-modal__eyebrow {
  color: #7d8799;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.instructor-modal__identity h2 {
  margin: 5px 0;
  color: #17213a;
  font-size: 25px;
}

.instructor-modal__title {
  margin: 0 0 12px;
  color: var(--primaryColor);
  font-weight: 600;
}

.instructor-modal__bio {
  margin: 0;
  color: #667085;
  line-height: 1.55;
}

.instructor-modal__trainings {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e3e8f0;
}
.instructor-training__status {
  padding: 5px 9px;
  border-radius: 999px;
  color: var(--primaryColor);
  background: #eef3ff;
  font-size: 12px;
}

.instructor-modal__trainings h3 {
  margin: 0 0 12px;
  color: #17213a;
  font-size: 18px;
}

.instructor-training-list {
  display: grid;
  gap: 9px;
}

.instructor-training {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 13px 14px;
  border: 1px solid #dce3ef;
  border-radius: 10px;
  color: #26324a;
  background: #f8fafc;
  text-decoration: none;
}

.instructor-training:hover {
  border-color: var(--primaryColor);
}

.instructor-modal__empty {
  margin: 0;
  padding: 18px;
  border-radius: 10px;
  color: #778198;
  background: #f8fafc;
  text-align: center;
}

.instructor-modal-fade-enter-active,
.instructor-modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.instructor-modal-fade-enter,
.instructor-modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 640px) {
  .instructor-modal {
    padding: 12px;
  }

  .instructor-modal__dialog {
    max-height: calc(100vh - 24px);
  }

  .instructor-modal__scroll-area {
    max-height: calc(100vh - 36px);
    padding: 16px 12px;
    padding-right: 20px;
  }

  .instructor-modal__profile {
    grid-template-columns: 1fr;
    padding-right: 0;
  }

  .instructor-modal__photo {
    width: 128px;
    height: 128px;
  }

}
</style>
